import { nextTick, ref } from "vue";
import { showToast } from "../../common.js";

/**
 * Shared CRUD state and helpers for assignment manager components.
 *
 * @param {object} options
 * @param {import('vue').Ref<HTMLElement|null>} options.primaryFieldRef - ref to the first form field to focus
 * @param {boolean} options.initialOpen - whether the form should start open
 */
export function useAssignmentCrud({ primaryFieldRef, initialOpen = false }) {
  const isSubmitting = ref(false);
  const deletingId = ref(0);
  const errorMessage = ref("");
  const isFormOpen = ref(initialOpen);

  async function focusPrimaryField() {
    await nextTick();
    const el = primaryFieldRef?.value;
    if (el instanceof HTMLElement) {
      el.focus();
    }
  }

  /**
   * Wrap an async mutation in submit-guard (sets isSubmitting, handles errors).
   * @param {() => Promise<void>} fn
   */
  async function withSubmit(fn) {
    if (isSubmitting.value) {
      return;
    }

    isSubmitting.value = true;
    errorMessage.value = "";

    try {
      await fn();
    } catch (error) {
      errorMessage.value = String(error?.message || "Nie udało się zapisać zmian.");
      showToast(errorMessage.value, { type: "danger" });
    } finally {
      isSubmitting.value = false;
    }
  }

  /**
   * Wrap an async delete in delete-guard (sets deletingId, handles errors).
   * @param {number} id
   * @param {() => Promise<void>} fn
   */
  async function withDelete(id, fn) {
    if (!id || deletingId.value === id) {
      return;
    }

    deletingId.value = id;
    errorMessage.value = "";

    try {
      await fn();
    } catch (error) {
      errorMessage.value = String(error?.message || "Nie udało się usunąć.");
      showToast(errorMessage.value, { type: "danger" });
    } finally {
      deletingId.value = 0;
    }
  }

  return {
    isSubmitting,
    deletingId,
    errorMessage,
    isFormOpen,
    focusPrimaryField,
    withSubmit,
    withDelete,
  };
}
